import "./EditLocationPage.scss";
import React from "react";
import { useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { Input, Skeleton, Button } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import CountryDropdown from "../../misc/CountryDropdown";
import StateDropdown from "../../misc/StateDropdown";
import { error, success } from "../../../../service/MessageService";
import EmailIcon from "../../../icons/EmailIcon";
import FacebookIcon from "../../../icons/FacebookIcon";
import InstagramIcon from "../../../icons/InstagramIcon";
import TwitterIcon from "../../../icons/TwitterIcon";
import WebsiteIcon from "../../../icons/WebsiteIcon";
import PhoneInput from "../../misc/PhoneInput";
import BusinessTypeDropdown from "../../misc/BusinessTypeDropdown";
import { AutocompleteGoogleSearch } from "./AutocompleteGoogleSearch";
import { UnitedStates } from "../../../../util/countries";
import AccountLocationApi from "../../../../api/AccountLocationApi";
import { env } from "../../../../env/env";

export function EditLocationPage() {
  const navigate = useNavigate();
  let { id } = useParams<{ id: string }>();

  const [internalId, setInternalId] = useState(0);
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState(UnitedStates);
  const [address2, setAddress2] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");

  const [isNameError, setNameError] = useState(false);
  const [isAddressError, setAddressError] = useState(false);
  const [isCityError, setCityError] = useState(false);
  const [isStateError, setStateError] = useState(false);

  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [instagram, setInstagram] = useState("");
  const [facebook, setFacebook] = useState("");
  const [twitter, setTwitter] = useState("");

  const [brandId, setBrandId] = useState("");

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;

    AccountLocationApi.getLocation(id).then((resp) => {
      const businessName = resp.data.name || "";
      setInternalId(resp.data.internalId || 0);
      setName(businessName.substring(0, 40));
      setPhone(resp.data.phone || "");
      setAddress(resp.data.address || "");
      setAddress2(resp.data.address2 || "");
      setCity(resp.data.city || "");
      setType(resp.data.type || "");
      setState(resp.data.state || "");
      setCountry(resp.data.country || "");
      setZip(resp.data.zip || "");
      setEmail(resp.data.email || "");
      setWebsite(resp.data.website || "");
      setInstagram(resp.data.instagram || "");
      setFacebook(resp.data.facebook || "");
      setTwitter(resp.data.twitter || "");
      setBrandId(resp.data.brandId || "");
      setLoading(false);
    });
  }, [id]);

  function save() {
    let hasErrors = validateFields();

    if (!hasErrors) {
      setNameError(false);
      setAddressError(false);
      setCityError(false);
      setStateError(false);
      setSaving(true);
      update();
    }
  }

  function validateFields() {
    let hasErrors = false;

    if (!name) {
      setNameError(true);
      hasErrors = true;
    }
    if (!address) {
      setAddressError(true);
      hasErrors = true;
    }
    if (!city) {
      setCityError(true);
      hasErrors = true;
    }
    if (!state) {
      setStateError(true);
      hasErrors = true;
    }

    return hasErrors;
  }

  function onCancel() {
    navigate(env.dashboardPath + "/account/locations");
  }

  function getLocation(): any {
    return {
      id,
      internalId,
      brandId,
      name,
      type,
      address,
      address2,
      city,
      state,
      country,
      zip,
      website,
      email,
      phone,
      facebook,
      instagram,
      twitter,
    };
  }

  function update() {
    AccountLocationApi.updateLocation(getLocation())
      .then(() => {
        setSaving(false);
        success("Location updated!");
        navigate(env.dashboardPath + "/account/locations");
      })
      .catch((ex: any) => {
        error(ex.response.data.message);
        setSaving(false);
      });
  }

  function updateAddress(value?: string, addressComponents?: any[]) {
    console.log("addressComponents ", addressComponents);
    if (addressComponents && addressComponents.length > 0) {
      addressComponents.forEach((addressComponent) => {
        if (addressComponent.types.find((type: string) => type === "country")) {
          const countryName =
            addressComponent.long_name === "United States"
              ? UnitedStates
              : addressComponent.long_name;
          setCountry(countryName);
        } else if (
          addressComponent.types.find((type: string) => type === "route")
        ) {
          const streetNo =
            addressComponents[0].types[0] === "street_number"
              ? addressComponents[0].long_name
              : "";
          setAddress(
            streetNo !== ""
              ? `${streetNo} ${addressComponent.long_name}`
              : addressComponent.long_name
          );
        } else if (
          addressComponent.types.find((type: string) => type === "locality")
        ) {
          setCity(addressComponent.long_name);
        } else if (
          addressComponent.types.find(
            (type: string) => type === "administrative_area_level_1"
          )
        ) {
          setState(addressComponent.long_name);
        } else if (
          addressComponent.types.find((type: string) => type === "postal_code")
        ) {
          setZip(addressComponent.long_name);
        }
      });
    } else {
      setAddress(value || "");
      setCity("");
      setState("");
    }
  }

  function updateAddress2(value?: string) {
    setAddress2(value || "");
  }

  return (
    <div className={"content edit-location-page"}>
      <NavLink to={env.dashboardPath + `/account/locations/`} className="back-link">
        <LeftOutlined /> My Locations
      </NavLink>

      <section>
        <h3>Update Location</h3>
        <p>
          Note: Changes will reflect on any Landing Pages or Ordering Menus for
          this location.
        </p>
      </section>

      {loading && <Skeleton />}

      {!loading && (
        <>
          <h4>Location</h4>
          <section>
            <div className={"location-info"}>
              <div className="form-item">
                <Input
                  value={name}
                  maxLength={40}
                  placeholder={"Business Name"}
                  size="large"
                  tabIndex={1}
                  type={"text"}
                  onChange={(ev) => setName(ev.target.value)}
                  className={isNameError ? 'has-error' : ''}
                />
                {isNameError && <div className="item-error">
                  Business name required
                </div>}
              </div>

              <div className="form-item">
                <BusinessTypeDropdown
                  tabIndex={2}
                  defaultValue={type}
                  onChange={(type) => {
                    setType(type);
                  }}
                />
              </div>

              <div className="form-item">
                <AutocompleteGoogleSearch
                  tabIndex={3}
                  fieldValue={address}
                  placeholder={"Street Address"}
                  updateField={updateAddress}
                  className={isAddressError ? 'has-error' : ''}
                />
                {isAddressError && <div className="item-error">
                  Address required
                </div>}
              </div>

              <div className="form-item">
                <AutocompleteGoogleSearch
                  tabIndex={4}
                  fieldValue={address2}
                  placeholder={"Street Address 2"}
                  updateField={updateAddress2}
                />
              </div>

              <div className="form-item">
                <Input
                  tabIndex={5}
                  placeholder={"City"}
                  size="large"
                  type={"text"}
                  value={city}
                  defaultValue={city}
                  onChange={(ev) => setCity(ev.target.value)}
                  className={isCityError ? 'has-error' : ''}
                />
                {isCityError && <div className="item-error">
                  City required
                </div>}
              </div>

              <div className="form-item">
                {country !== UnitedStates && (
                  <Input
                    tabIndex={6}
                    placeholder={"State/Province"}
                    size="large"
                    type={"text"}
                    value={state}
                    defaultValue={state}
                    onChange={(ev) => setState(ev.target.value)}
                    className={isStateError ? 'has-error' : ''}
                  />
                )}
                {country === UnitedStates && (
                  <StateDropdown
                    tabIndex={6}
                    defaultValue={state}
                    onChange={(state) => {
                      setState(state);
                    }}
                    className={isStateError ? 'has-error' : ''}
                  />
                )}
                {isStateError && <div className="item-error">
                  State required
                </div>}
              </div>

              <div className="form-item">
                <CountryDropdown
                  tabIndex={7}
                  defaultValue={country}
                  onChange={(country) => {
                    setCountry(country);
                  }}
                />
              </div>

              <div className="form-item">
                <Input
                  tabIndex={8}
                  placeholder={"Zip/Postal Code"}
                  type={"text"}
                  value={zip}
                  size="large"
                  onChange={(ev) => setZip(ev.target.value)}
                />
              </div>
            </div>
          </section>

          <h4>Contact Links</h4>
          <div className={"location-social"}>
            <Input
              tabIndex={9}
              value={website}
              onChange={(ev) => setWebsite(ev.target.value)}
              addonBefore={<WebsiteIcon />}
              placeholder="https://www.your-website.com"
              size="large"
            />
            <PhoneInput
              tabIndex={10}
              value={phone}
              onChange={(phone) => setPhone(phone)}
              country={country}
              showAddon={true}
            />
            <Input
              tabIndex={11}
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              addonBefore={<EmailIcon />}
              placeholder="you@youremail.com"
              size="large"
            />
            <Input
              tabIndex={12}
              value={facebook}
              onChange={(ev) => setFacebook(ev.target.value)}
              addonBefore={<FacebookIcon />}
              placeholder="facebook.com/yourpage"
              size="large"
            />
            <Input
              tabIndex={13}
              value={twitter}
              onChange={(ev) => setTwitter(ev.target.value)}
              addonBefore={<TwitterIcon />}
              placeholder="@yourhandle"
              size="large"
            />
            <Input
              tabIndex={14}
              value={instagram}
              onChange={(ev) => setInstagram(ev.target.value)}
              addonBefore={<InstagramIcon />}
              placeholder="@yourhandle"
              size="large"
            />
          </div>
        </>
      )}

      <footer>
        <Button className={"cancel"} onClick={onCancel} size={"large"}>
          <span>Cancel</span>
        </Button>
        <Button loading={saving} onClick={save} className={"primary"} size={"large"}>
          Save
        </Button>
      </footer>
    </div>
  );
}
